/**
 * ActionCable 기반 Yjs Provider
 *
 * y-websocket과 동일한 역할을 ActionCable consumer로 대체한다.
 * 프로토콜: y-protocols binary를 base64 encode하여 JSON envelope 안에 전송.
 *
 * 메시지 타입:
 *   sync-step-1  — 클라이언트 → 서버 (state_vector)
 *   sync-step-2  — 서버 → 클라이언트 (full/diff update)
 *   y-update     — 양방향 (incremental update)
 *   awareness    — 양방향 (cursor/presence)
 *   presence     — 서버 → 클라이언트 (join/leave)
 */
import * as Y from 'yjs'
import { createConsumer } from '@rails/actioncable'
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness'
import { seedToColor } from './color_utils'

const consumer = createConsumer()

export class CableProvider {
  /**
   * @param {Object} options
   * @param {Y.Doc} options.doc
   * @param {Awareness} options.awareness
   * @param {string} options.recordType - 'Post' | 'Page'
   * @param {number} options.recordId
   * @param {Function} [options.onPresence] - presence 이벤트 콜백
   * @param {Function} [options.onSynced] - 초기 sync 완료 콜백
   * @param {Function} [options.onStatus] - 연결 상태 변경 콜백
   */
  constructor({ doc, awareness, recordType, recordId, editorId, onPresence, onSynced, onStatus, onConnected }) {
    this.doc = doc
    this.awareness = awareness
    this.recordType = recordType
    this.recordId = recordId
    this.onPresence = onPresence
    this.onSynced = onSynced
    this.onStatus = onStatus
    this.onConnected = onConnected

    this.synced = false
    this.subscription = null
    this._editorId = editorId != null ? String(editorId) : null
    this._connectionUid = null // 서버에서 할당하는 접속별 고유 ID (echo 방지용)
    // 서버가 subscribe를 confirm하기 전까지 false. rejected/disconnected 시 다시 false.
    // Awareness가 자체 heartbeat로 3~15s 마다 local state를 재갱신하므로, confirm 전에
    // subscription.perform을 호출하면 서버는 "Unable to find subscription"으로 에러 로그를
    //남긴다. 이 플래그로 _send를 gating한다.
    this._confirmed = false

    this._setupDocListener()
    this._setupAwarenessListener()
  }

  connect() {
    this.onStatus?.('connecting')

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'NoteCollaborationChannel',
        record_type: this.recordType,
        record_id: this.recordId,
      },
      {
        connected: () => this._onConnected(),
        disconnected: () => this._onDisconnected(),
        rejected: () => this._onRejected(),
        received: (data) => this._onReceived(data),
      }
    )
  }

  disconnect() {
    // 먼저 confirm 플래그를 내려 awareness setLocalState(null) 트리거가 서버로 가지 않게 한다.
    // (rejected 경로에서 destroy→disconnect를 탈 때 불필요한 perform 송출을 방지하기 위한 guard.)
    this._confirmed = false

    // 자기 awareness 상태 제거 (다른 사용자에게 커서가 남지 않도록)
    this.awareness.setLocalState(null)

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.synced = false
    this._connectionUid = null
    this.onStatus?.('disconnected')
  }

  destroy() {
    this.disconnect()
    if (this._sendTimer) clearTimeout(this._sendTimer)
    this.doc.off('update', this._docUpdateHandler)
    this.awareness.off('update', this._awarenessUpdateHandler)
  }

  get editorId() {
    return this._editorId
  }

  // --- Private ---

  _setupDocListener() {
    this._pendingUpdates = []
    this._sendTimer = null

    this._docUpdateHandler = (update, origin) => {
      if (origin === this) return

      // 200ms debounce — 빠른 타이핑 시 update를 모아서 한 번에 전송
      this._pendingUpdates.push(update)
      if (!this._sendTimer) {
        this._sendTimer = setTimeout(() => {
          this._sendTimer = null
          if (this._pendingUpdates.length === 0) return

          const merged = this._pendingUpdates.length === 1
            ? this._pendingUpdates[0]
            : Y.mergeUpdates(this._pendingUpdates)
          this._pendingUpdates = []
          this._send({ type: 'y-update', update: this._encodeUpdate(merged) })
        }, 200)
      }
    }
    this.doc.on('update', this._docUpdateHandler)
  }

  _setupAwarenessListener() {
    this._awarenessUpdateHandler = ({ added, updated, removed }, origin) => {
      if (origin === 'remote') return

      const changedClients = added.concat(updated).concat(removed)
      const update = encodeAwarenessUpdate(this.awareness, changedClients)
      const base64 = this._encodeUpdate(update)
      this._send({ type: 'awareness', state: base64 })
    }
    this.awareness.on('update', this._awarenessUpdateHandler)
  }

  _onConnected() {
    this._confirmed = true
    this.onStatus?.('connected')

    // sync-step-1: state vector 전송
    const sv = Y.encodeStateVector(this.doc)
    this._send({ type: 'sync-step-1', state_vector: this._encodeUpdate(sv) })
  }

  _onDisconnected() {
    this._confirmed = false
    this.synced = false
    // 재연결 시 서버가 새 connection_uid를 발급하므로, 이전 uid를 들고 있으면
    // takeover broadcast(target_uid=OLD)가 자기 자신과 매치되는 race가 발생한다.
    // 새 'connected' 메시지 수신 전까지 filter를 안전하게 무효화.
    this._connectionUid = null
    this.onStatus?.('disconnected')
  }

  // 서버가 구독을 거절한 경우 (auth/feature_flag 실패 등).
  // Awareness heartbeat가 이미 rejected된 subscription에 perform을 계속 호출해서
  // 서버 로그가 오염되지 않도록 provider 전체를 정리한다.
  _onRejected() {
    this._confirmed = false
    this._connectionUid = null
    this.onStatus?.('rejected')
    this.destroy()
  }

  _onReceived(data) {
    switch (data.type) {
      case 'connected':
        this._connectionUid = data.connection_uid
        // awareness color를 connection_uid 기반으로 통일 (presence panel과 caret 색상 일치)
        if (this.awareness) {
          const currentUser = this.awareness.getLocalState()?.user || {}
          this.awareness.setLocalStateField('user', {
            ...currentUser,
            color: seedToColor(data.connection_uid),
          })
        }
        this.onConnected?.(data)
        break
      case 'sync-step-2':
        this._handleSyncStep2(data)
        break
      case 'y-update':
        this._handleUpdate(data)
        break
      case 'awareness':
        this._handleAwareness(data)
        break
      case 'presence':
        this._handlePresence(data)
        break
      case 'session-takeover':
        this._handleSessionTakeover(data)
        break
    }
  }

  // 서버가 강제 로그아웃/계정 만료 시 broadcast — 현재 자동 trigger 경로 없음, 유지만.
  _handleSessionTakeover(data) {
    // 다른 연결이 자기 자리를 대체했을 때만 반응. 타인 takeover 메시지는 무시.
    if (!this._connectionUid || data.target_uid !== this._connectionUid) return

    this.disconnect()

    document.dispatchEvent(new CustomEvent('session:expired', {
      detail: { reason: 'collab_takeover' },
    }))
  }

  _handleSyncStep2(data) {
    const update = this._decodeUpdate(data.update)
    if (!update) return

    Y.applyUpdate(this.doc, update, this)

    if (!this.synced) {
      this.synced = true
      this.onSynced?.()
    }
  }

  _isOwnMessage(data) {
    return this._connectionUid && data.sender === this._connectionUid
  }

  _handleUpdate(data) {
    if (this._isOwnMessage(data)) return

    const update = this._decodeUpdate(data.update)
    if (!update) return

    Y.applyUpdate(this.doc, update, this)
  }

  _handleAwareness(data) {
    if (this._isOwnMessage(data)) return

    const update = this._decodeUpdate(data.state)
    if (!update) return

    applyAwarenessUpdate(this.awareness, update, 'remote')
  }

  _handlePresence(data) {
    if (data.action === 'leave' && data.editor_id) {
      // 떠난 editor의 stale awareness 상태 제거
      // (새로고침 시 이전 clientID의 커서가 남는 문제 해결)
      this._removeAwarenessForEditor(data.editor_id)
    }
    this.onPresence?.(data)
  }

  /**
   * 특정 editorId에 해당하는 모든 awareness 상태를 제거
   */
  _removeAwarenessForEditor(editorId) {
    const staleClientIds = []
    this.awareness.getStates().forEach((state, clientId) => {
      if (clientId === this.awareness.clientID) return // 자기 자신은 건너뜀
      if (state?.user?.editorId == editorId) {
        staleClientIds.push(clientId)
      }
    })
    if (staleClientIds.length > 0) {
      removeAwarenessStates(this.awareness, staleClientIds, 'remote')
    }
  }

  _send(data) {
    if (!this.subscription || !this._confirmed) return
    this.subscription.perform('receive', data)
  }

  _encodeUpdate(uint8array) {
    // Uint8Array → base64
    let binary = ''
    const bytes = uint8array instanceof Uint8Array ? uint8array : new Uint8Array(uint8array)
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }

  _decodeUpdate(base64) {
    if (!base64) return null
    try {
      const binary = atob(base64)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
      }
      return bytes
    } catch {
      return null
    }
  }
}
